/**
 * Admin Questions Management
 * Inline editor — pillar tabs, per-row type/text/weight/routing editing.
 * Text-only changes → direct Supabase UPDATE (no versioning)
 * Structural changes (type, routing) → edge function PUT (versioning + audit)
 */

import { createFileRoute, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import React, { useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { api } from '@/api/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, X, ChevronDown, ChevronUp, Plus, ExternalLink } from 'lucide-react';

// ========================================
// Types
// ========================================

type QuestionType = 'scale_5' | 'scale_10' | 'yesno' | 'numeric' | 'trend' | 'select';

interface DiagnosticQuestion {
  id: string;
  code: string;
  pillar_id: string;
  question_text: string;
  type: QuestionType;
  weight: number;
  help_text: string | null;
  routing: string;
  scoring_rules: Record<string, any>;
  display_order: number;
  version: number;
  is_active: boolean;
  effective_until: string | null;
  pillar?: {
    code: string;
    name: string;
    weight: number;
  };
}

interface QuestionEdits {
  question_text?: string;
  type?: QuestionType;
  weight?: number;
  help_text?: string | null;
  routing?: string;
}

// ========================================
// Constants
// ========================================

const STRUCTURAL_FIELDS = new Set(['type', 'routing']);

const DEFAULT_SCORING_RULES: Record<QuestionType, Record<string, any>> = {
  scale_5: { type: 'formula', formula: 'value * 20' },
  scale_10: { type: 'formula', formula: 'value * 10' },
  yesno: { type: 'lookup', values: { no: 0, yes: 100 } },
  numeric: { type: 'ranges', ranges: [] },
  trend: { type: 'lookup', values: { down: 0, flat: 50, up: 100 } },
  // `select` scoring comes from diagnostic_answer_options.score — resolved
  // server-side via option UUID lookup. Default is empty.
  select: {},
};

const TYPE_COLORS: Record<QuestionType, string> = {
  scale_5: 'bg-purple-500/15 text-purple-400',
  scale_10: 'bg-blue-500/15 text-blue-400',
  yesno: 'bg-green-500/15 text-green-400',
  numeric: 'bg-orange-500/15 text-orange-400',
  trend: 'bg-cyan-500/15 text-cyan-400',
  select: 'bg-slate-500/15 text-slate-400',
};

const TYPE_LABELS: Record<QuestionType, string> = {
  scale_5: '1-5 Scale',
  scale_10: '1-10 Scale',
  yesno: 'Yes / No',
  numeric: 'Numeric',
  trend: 'Trend',
  select: 'Multi-choice',
};

const ROUTING_OPTIONS = [
  { value: 'all', label: 'All Stages' },
  { value: 'Startup+', label: 'Startup+' },
  { value: 'Buildup+', label: 'Buildup+' },
  { value: 'Scale-up+', label: 'Scale-up+' },
  { value: 'Systemise+', label: 'Systemise+' },
];

const QUESTION_TYPES: QuestionType[] = ['scale_5', 'scale_10', 'yesno', 'numeric', 'trend', 'select'];

// ========================================
// Route
// ========================================

export const Route = createFileRoute('/admin/diagnostic/questions')({
  component: QuestionsRoot,
});

function QuestionsRoot() {
  const matches = useRouterState({ select: (s) => s.matches });
  const isOnChild = matches.some((m) => m.routeId.includes('$id'));
  if (isOnChild) return <Outlet />;
  return <QuestionEditor />;
}

// ========================================
// Question Editor
// ========================================

function QuestionEditor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['diagnostic-admin-questions'],
    queryFn: async () => {
      const { data: questions, error } = await supabase
        .from('diagnostic_questions')
        .select('*, pillar:diagnostic_pillars!inner(code, name, weight)')
        .eq('is_active', true)
        .is('effective_until', null)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return { questions: questions as DiagnosticQuestion[] };
    },
    staleTime: 5 * 60 * 1000,
  });
  const [edits, setEdits] = useState<Record<string, QuestionEdits>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');

  const questions = data?.questions || [];

  // Group by pillar
  const pillarGroups = useMemo(() => {
    const groups: Record<string, { name: string; questions: DiagnosticQuestion[] }> = {};
    for (const q of questions) {
      const code = q.pillar?.code || 'unknown';
      if (!groups[code]) {
        groups[code] = { name: q.pillar?.name || code, questions: [] };
      }
      groups[code].questions.push(q);
    }
    for (const group of Object.values(groups)) {
      group.questions.sort((a, b) => a.display_order - b.display_order);
    }
    return groups;
  }, [questions]);

  // Track which pillars have edits for tab indicators
  const pillarHasEdits = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const [qId] of Object.entries(edits)) {
      const q = questions.find((q) => q.id === qId);
      if (q?.pillar?.code) result[q.pillar.code] = true;
    }
    return result;
  }, [edits, questions]);

  const pillarCodes = Object.keys(pillarGroups).sort();
  const currentTab = activeTab || pillarCodes[0] || '';

  const editCount = Object.keys(edits).length;
  const versionedCount = Object.values(edits).filter((changes) =>
    Object.keys(changes).some((f) => STRUCTURAL_FIELDS.has(f)),
  ).length;
  const hasEdits = editCount > 0;

  // ── Field edit handler ──

  const handleFieldEdit = (questionId: string, field: keyof QuestionEdits, value: any) => {
    const original = questions.find((q) => q.id === questionId);
    if (!original) return;

    const originalValue = original[field];
    const isReverted = value === originalValue || (value === '' && originalValue === null);

    setEdits((prev) => {
      const existing = prev[questionId] || {};

      if (isReverted) {
        const next = { ...existing };
        delete next[field];
        if (Object.keys(next).length === 0) {
          const all = { ...prev };
          delete all[questionId];
          return all;
        }
        return { ...prev, [questionId]: next };
      }

      return { ...prev, [questionId]: { ...existing, [field]: value } };
    });
  };

  // ── Save handler ──

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(edits);

      for (const [questionId, changes] of entries) {
        const changedFields = Object.keys(changes) as (keyof QuestionEdits)[];
        const hasStructural = changedFields.some((f) => STRUCTURAL_FIELDS.has(f));

        if (hasStructural) {
          const payload: Record<string, any> = { ...changes };
          if (changes.type) {
            payload.scoring_rules = DEFAULT_SCORING_RULES[changes.type];
          }
          await api.put(`/admin-diagnostic/questions/${questionId}`, payload);
        } else {
          const { error } = await supabase
            .from('diagnostic_questions')
            .update(changes)
            .eq('id', questionId);
          if (error) {
            console.error('Save failed for', questionId, error);
            return;
          }
        }
      }

      setEdits({});
      await queryClient.invalidateQueries({ queryKey: ['diagnostic-admin-questions'] });
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEdits({});
  };

  const toggleExpand = (questionId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading questions...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed header */}
      <div className="flex-shrink-0 bg-card">
        {/* Title + save bar */}
        <div className="border-b border-border px-6 py-3 flex items-center gap-4">
          <h1 className="text-lg font-bold text-foreground">Diagnostic Questions</h1>
          <button
            onClick={() => navigate({ to: '/admin/diagnostic/questions/$id', params: { id: 'new' } })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
          {hasEdits && (
            <>
              <span className="text-sm text-muted-foreground">
                {editCount} unsaved {editCount === 1 ? 'change' : 'changes'}
                {versionedCount > 0 && (
                  <span className="text-amber-500"> ({versionedCount} versioned)</span>
                )}
              </span>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
        </div>

        {/* Pillar tabs */}
        <div className="border-b border-border px-6">
          <div className="flex gap-1 overflow-x-auto">
            {pillarCodes.map((code) => {
              const isActive = currentTab === code;
              const hasChanges = pillarHasEdits[code];
              return (
                <button
                  key={code}
                  onClick={() => setActiveTab(code)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? hasChanges
                        ? 'border-amber-500 text-amber-500'
                        : 'border-primary text-primary'
                      : hasChanges
                        ? 'border-transparent text-amber-500/80 hover:text-amber-500'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {pillarGroups[code].name}
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({pillarGroups[code].questions.length})
                  </span>
                  {hasChanges && (
                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Column headers */}
        <div className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6">
            <div className="px-3 py-2 border-l-2 border-l-transparent flex items-center gap-3">
              <span className="w-10 flex-shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">Code</span>
              <span className="w-24 flex-shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">Type</span>
              <span className="flex-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 pl-2">Question</span>
              <span className="w-8 flex-shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 text-center">Ver</span>
              <span className="w-14 flex-shrink-0 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 text-center">Wt</span>
              <span className="w-6 flex-shrink-0" />
              <span className="w-6 flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>

      {/* Question list — only this scrolls */}
      <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-4 space-y-2">
        {pillarGroups[currentTab]?.questions.map((q) => {
          const qEdits = edits[q.id];
          const isEdited = qEdits !== undefined;
          const isExpanded = expandedRows.has(q.id);

          const displayText = qEdits?.question_text ?? q.question_text;
          const displayType = qEdits?.type ?? q.type;
          const displayWeight = qEdits?.weight ?? q.weight;
          const displayRouting = qEdits?.routing ?? q.routing;
          const displayHelpText = qEdits?.help_text !== undefined ? (qEdits.help_text ?? '') : (q.help_text ?? '');

          return (
            <div
              key={q.id}
              className={`flex flex-col gap-1 p-3 rounded-lg transition-colors ${
                isEdited
                  ? 'bg-amber-500/5 border-l-2 border-l-amber-500'
                  : 'border-l-2 border-l-transparent'
              }`}
            >
              {/* Line 1 */}
              <div className="flex items-center gap-3">
                {/* Code */}
                <span className="w-10 flex-shrink-0 text-xs font-mono text-muted-foreground">
                  {q.code}
                </span>

                {/* Type select */}
                <div className="w-24 flex-shrink-0 relative">
                  <select
                    value={displayType}
                    onChange={(e) => handleFieldEdit(q.id, 'type', e.target.value as QuestionType)}
                    className={`w-full text-xs pl-2 pr-6 py-1 rounded-md border-0 outline-none cursor-pointer appearance-none ${TYPE_COLORS[displayType]}`}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-50" />
                </div>

                {/* Question text */}
                <input
                  type="text"
                  value={displayText}
                  onChange={(e) => handleFieldEdit(q.id, 'question_text', e.target.value)}
                  className="flex-1 bg-muted/30 text-foreground text-sm outline-none border-b-2 border-muted-foreground/25 focus:border-primary rounded-t-sm px-2 py-1.5"
                />

                {/* Version */}
                <span className="w-8 flex-shrink-0 text-xs text-muted-foreground/50 text-center">
                  (v{q.version})
                </span>

                {/* Weight */}
                <input
                  type="number"
                  value={displayWeight}
                  onChange={(e) => handleFieldEdit(q.id, 'weight', parseFloat(e.target.value) || 0)}
                  step="0.5"
                  min="0"
                  max="20"
                  className="w-14 flex-shrink-0 text-xs text-center bg-muted/30 text-foreground outline-none border-b border-muted-foreground/25 focus:border-primary py-1 rounded-t-sm"
                />

                {/* Expand toggle */}
                <button
                  onClick={() => toggleExpand(q.id)}
                  className="w-6 flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {/* Full editor link */}
                <button
                  onClick={() => navigate({ to: '/admin/diagnostic/questions/$id', params: { id: q.id } })}
                  className="w-6 flex-shrink-0 text-muted-foreground/40 hover:text-foreground transition-colors"
                  title="Open full editor"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Line 2 — expanded */}
              {isExpanded && (
                <div className="flex items-center gap-3 pb-1">
                  {/* Spacer for code column */}
                  <div className="w-10 flex-shrink-0" />

                  {/* Routing select */}
                  <select
                    value={displayRouting}
                    onChange={(e) => handleFieldEdit(q.id, 'routing', e.target.value)}
                    className="w-24 flex-shrink-0 text-xs px-2 py-1 bg-secondary border border-border rounded-md text-foreground outline-none cursor-pointer"
                  >
                    {ROUTING_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>

                  {/* Help text */}
                  <input
                    type="text"
                    value={displayHelpText}
                    onChange={(e) =>
                      handleFieldEdit(q.id, 'help_text', e.target.value || null)
                    }
                    placeholder="Add help text..."
                    className="flex-1 bg-muted/30 text-foreground text-sm outline-none border-b border-muted-foreground/25 focus:border-primary rounded-t-sm px-2 py-1.5 placeholder:text-muted-foreground/40"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
